// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ShopCommands from '@app/features/shop/commands/ShopCommands';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {inPayableUnits} from '@app/features/shop/utils/ShopPriceFormat';
import {normalizeUsername} from '@app/features/user/hooks/useUsernameSelectionAvailability';
import type {
	ShopPriceResponse,
	UsernameCardAvailabilityStatus,
} from '@fluxer/schema/src/domains/shop/ShopResponseSchemas';
import {useEffect, useState} from 'react';

// One request 350 ms after the last key, never one per key.
const CHECK_DELAY_MS = 350;

export interface UsernameCardAvailability {
	// idle: nothing typed; checking: a name of the card's length waits for its answer; error: the check failed.
	state: 'idle' | 'checking' | 'error' | UsernameCardAvailabilityStatus;
	// The name as checked: trimmed, without the @, lowercase.
	username: string;
	// What this name costs this viewer, in payable units; null where it is not sold to them or not answered yet.
	price: ShopPriceResponse | null;
	// The viewer may take this name free with the Visionary username right.
	visionaryFree: boolean;
	// held: when another buyer's open order stops holding the name if left unpaid.
	heldUntil?: string;
	// held: a payment for the name is being confirmed.
	awaitingPayment?: boolean;
}

type Answer = Omit<UsernameCardAvailability, 'username'> & {username: string; refresh: number};

// Only a name of exactly the card's length in [a-z0-9] is asked about; anything else is invalid without a request.
// A new `refresh` drops the answer on screen and asks again (the order refused the name meanwhile).
export function useUsernameCardAvailability(
	value: string,
	length: number | null,
	refresh = 0,
): UsernameCardAvailability {
	const username = normalizeUsername(value);
	const valid = length !== null && username.length === length && /^[a-z0-9]+$/.test(username);
	// Moves when another buyer's hold ends, to ask about the name again.
	const [recheck, setRecheck] = useState(0);
	const [answer, setAnswer] = useState<Answer>({
		username: '',
		refresh,
		state: 'idle',
		price: null,
		visionaryFree: false,
	});
	useEffect(() => {
		if (!valid) return;
		const controller = new AbortController();
		const timer = window.setTimeout(() => {
			ShopCommands.checkUsernameCard(username, controller.signal).then(
				({status, price, visionary_free, held_until, awaiting_payment}) => {
					if (controller.signal.aborted) return;
					setAnswer({
						username,
						refresh,
						state: status,
						// A username price has no member discount.
						price: price ? inPayableUnits(price, false, ShopCatalog.settings?.try_to_azn_rate_micro) : null,
						visionaryFree: visionary_free === true,
						...(held_until ? {heldUntil: held_until} : {}),
						...(awaiting_payment ? {awaitingPayment: true} : {}),
					});
				},
				() => {
					if (!controller.signal.aborted)
						setAnswer({username, refresh, state: 'error', price: null, visionaryFree: false});
				},
			);
		}, CHECK_DELAY_MS);
		return () => {
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [valid, username, refresh, recheck]);
	const heldUntil = answer.username === username ? answer.heldUntil : undefined;
	useEffect(() => {
		if (!heldUntil) return;
		const timer = window.setTimeout(
			() => setRecheck((count) => count + 1),
			Math.max(0, Date.parse(heldUntil) - Date.now()) + 1000,
		);
		return () => window.clearTimeout(timer);
	}, [heldUntil]);
	const none = {price: null, visionaryFree: false};
	if (length === null || !username) return {state: 'idle', username, ...none};
	if (!valid) return {state: 'invalid', username, ...none};
	if (answer.username !== username || answer.refresh !== refresh) return {state: 'checking', username, ...none};
	const {refresh: _refresh, ...shown} = answer;
	return shown;
}
